// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  components/BotaoExportar.tsx
//  Botao com dropdown para exportar relatórios em Excel ou CSV.
// ════════════════════════════════════════════════════════════════════════════

import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useExportacao, type FormatoExportacao } from '@/hooks/useExportacao';

interface BotaoExportarProps {
  /** URL base do endpoint de exportação */
  endpoint: string;
  /** Parâmetros de filtro atuais (serão enviados como query params) */
  params?: Record<string, string | number | boolean | undefined | null>;
  /** Variante do botão */
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  /** Tamanho do botão */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  /** Texto do botão (default: "Exportar") */
  label?: string;
  /** Se true, mostra apenas como ícone (sem texto) */
  iconOnly?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

export function BotaoExportar({
  endpoint,
  params,
  variant = 'outline',
  size = 'sm',
  label = 'Exportar',
  iconOnly = false,
  className,
}: BotaoExportarProps) {
  const { exportar, isExporting } = useExportacao({ endpoint, params });

  const handleExport = (formato: FormatoExportacao) => {
    exportar(formato);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={isExporting}
          className={className}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {!iconOnly && <span className="ml-2">{isExporting ? 'Exportando...' : label}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport('xlsx')}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('csv')}>
          <FileText className="h-4 w-4 mr-2" />
          CSV (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
